/* eslint-disable no-console -- Console monkey patching inside logging bootstrap is intentional */
// Console silencer (client app): now works in prod AND optional in dev.
// Rules:
//  - Prod default: silenced (log/info/debug muted) unless VITE_DEBUG_LOGS=1 or LS_DEBUG_LOGS=1.
//  - Dev default: NOT silenced (keep logs) unless LS_SILENCE_LOGS=1.
//  - LS_DEBUG_LOGS=1 always forces logs on (overrides silence keys).
//  - LS_SILENCE_LOGS=1 forces silence (unless LS_DEBUG_LOGS=1) both dev & prod.
//  - window.enableLSDebugLogs(): set LS_DEBUG_LOGS=1, remove LS_SILENCE_LOGS.
//  - window.disableLSDebugLogs(): in prod -> silence (remove LS_DEBUG_LOGS); in dev -> sets LS_SILENCE_LOGS=1.
//  - window.silenceLogsDev()/window.unsilenceLogsDev() explicit control (both envs) if needed.

declare global {
  // Расширяем Window для пользовательских функций управления логами
  interface Window {
    enableLSDebugLogs?: () => void;
    disableLSDebugLogs?: () => void;
    silenceLogsDev?: () => void;
    unsilenceLogsDev?: () => void;
  }
  // Расширяем ImportMetaEnv для типобезопасного доступа к переменной
  interface ImportMetaEnv {
    readonly VITE_DEBUG_LOGS?: string;
  }
}

((): void => {
  // Используем отдельный объект-хранилище флага вместо произвольного any
  const globalObj = globalThis as Record<string, unknown>;
  if (globalObj.__LS_LOG_PATCHED_CLIENT__ === true) return;
  globalObj.__LS_LOG_PATCHED_CLIENT__ = true;

  const isProd = import.meta.env.PROD;
  const envFlag = import.meta.env.VITE_DEBUG_LOGS === "1";

  const original = {
    log: console.log,
    info: console.info,
    debug: console.debug,
  };
  const noop = (): void => {};

  const applySilence = (): void => {
    console.log = noop;
    console.info = noop;
    console.debug = noop;
  };
  const restore = (): void => {
    console.log = original.log;
    console.info = original.info;
    console.debug = original.debug;
  };

  const ls = (k: string): string | null => {
    try {
      return localStorage.getItem(k);
    } catch {
      return null;
    }
  };
  const debugOn = (): boolean => envFlag || ls("LS_DEBUG_LOGS") === "1";
  const forceSilence = (): boolean => ls("LS_SILENCE_LOGS") === "1";
  const shouldSilence = (): boolean => {
    if (debugOn()) return false;
    if (forceSilence()) return true;
    if (isProd) return true; // prod default silence
    return false; // dev default show
  };
  const reevaluate = (): void => {
    (shouldSilence() ? applySilence : restore)();
  };

  // Initial apply
  reevaluate();

  window.enableLSDebugLogs = (): void => {
    try {
      localStorage.setItem("LS_DEBUG_LOGS", "1");
      localStorage.removeItem("LS_SILENCE_LOGS");
    } catch {}
    reevaluate();

    original.log("[logging] debug ON");
  };
  window.disableLSDebugLogs = (): void => {
    try {
      // Remove debug flag; if dev, set explicit silence flag, in prod silence happens by default.
      localStorage.removeItem("LS_DEBUG_LOGS");
      if (!isProd) localStorage.setItem("LS_SILENCE_LOGS", "1");
    } catch {}
    reevaluate();
  };
  window.silenceLogsDev = (): void => {
    try {
      localStorage.setItem("LS_SILENCE_LOGS", "1");
    } catch {}
    reevaluate();
  };
  window.unsilenceLogsDev = (): void => {
    try {
      localStorage.removeItem("LS_SILENCE_LOGS");
    } catch {}
    reevaluate();
  };
  // Optional: react to storage events (multi-tab consistency)
  window.addEventListener("storage", (e: StorageEvent): void => {
    if (e.key === "LS_DEBUG_LOGS" || e.key === "LS_SILENCE_LOGS") reevaluate();
  });
})();
export {};
